import React from 'react';
import { AlertTriangle, Send } from 'lucide-react';
import Modal from '../../Common/Components/Modal';

interface PublishTermsDialogProps {
  show: boolean;
  currentVersion: string | null;
  nextVersion: string;
  isPublishing: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Publishing is irreversible from the user's side: the app compares the stored
 * `termsAcceptedVersion` against the newest published version, so a bump makes
 * every signed-in user accept again before they can continue. That consequence
 * is spelled out here rather than buried in a tooltip.
 *
 * Tinted surfaces follow the theme formula used across the layout customizer:
 * bg-{colour}-50 / border-{colour}-200 / text-{colour}-500, and in dark mode
 * bg-{colour}-500/10 / border-{colour}-500/30. `custom` is the theme's brand
 * scale and `orange` its warning scale.
 */
const PublishTermsDialog: React.FC<PublishTermsDialogProps> = ({
  show,
  currentVersion,
  nextVersion,
  isPublishing,
  onClose,
  onConfirm,
}) => {
  const handleClose = () => {
    if (!isPublishing) onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} id="publishTermsModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[32rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Publish Terms &amp; Conditions</Modal.Title>
      </Modal.Header>

      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className="px-3 py-1.5 rounded-md border border-slate-200 bg-slate-50 text-slate-500 font-medium dark:bg-zink-500 dark:border-zink-400 dark:text-zink-200">
            {currentVersion ?? 'none'}
          </span>
          <span className="text-slate-400 dark:text-zink-200">&rarr;</span>
          <span className="px-3 py-1.5 rounded-md border border-custom-200 bg-custom-50 text-custom-500 font-medium dark:bg-custom-500/10 dark:border-custom-500/30 dark:text-custom-500">
            {nextVersion}
          </span>
        </div>

        <div className="flex gap-3 p-3 rounded-md border border-orange-200 bg-orange-50 text-orange-500 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-500">
          <AlertTriangle className="size-5 shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium mb-1">Every user will be asked to accept again.</p>
            <p>
              This version goes live in the mobile app immediately. Anyone signed
              in must read and accept {nextVersion} before they can keep using the
              app.
            </p>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={handleClose}
          disabled={isPublishing}
          className="px-4 py-2 text-slate-600 dark:text-zink-300 border border-slate-300 dark:border-zink-500 rounded-md hover:bg-slate-50 dark:hover:bg-zink-600 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={isPublishing}
          className="px-4 py-2 bg-custom-500 text-white rounded-md hover:bg-custom-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isPublishing ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Send className="size-4" />
          )}
          Publish {nextVersion}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PublishTermsDialog;
